/**
 * Utilitários para Administração do Sistema
 * Funções para gerenciar usuários, permissões, estatísticas e relatórios
 */

// Níveis de acesso disponíveis no sistema
export const ACCESS_LEVELS = {
  'user': {
    label: 'Usuário',
    description: 'Usuário comum do sistema',
    permissions: ['create_ticket', 'view_own_tickets', 'update_profile'],
    color: 'blue',
    icon: 'Users'
  },
  'suporte': {
    label: 'Suporte',
    description: 'Atendimento e suporte geral',
    permissions: ['view_tickets', 'respond_tickets', 'assign_tickets', 'view_user_info'],
    color: 'green',
    icon: 'Headphones'
  },
  'suporte_compras': {
    label: 'Suporte Compras',
    description: 'Atendimento de compras e doações',
    permissions: ['view_tickets', 'respond_tickets', 'assign_tickets', 'view_user_info', 'access_payment_info'],
    color: 'blue',
    icon: 'Headphones'
  },
  'suporte_descontos': {
    label: 'Suporte Descontos',
    description: 'Atendimento de cupons e descontos',
    permissions: ['view_tickets', 'respond_tickets', 'assign_tickets', 'view_user_info', 'manage_discounts'],
    color: 'purple',
    icon: 'Headphones'
  },
  'moderador': {
    label: 'Moderador',
    description: 'Moderação e denúncias',
    permissions: ['view_tickets', 'respond_tickets', 'assign_tickets', 'view_user_info', 'moderate_content', 'ban_users'],
    color: 'yellow',
    icon: 'Shield'
  },
  'super_moderador': {
    label: 'Super Moderador',
    description: 'Moderação avançada e contestações',
    permissions: ['view_tickets', 'respond_tickets', 'assign_tickets', 'view_user_info', 'moderate_content', 'ban_users', 'handle_appeals'],
    color: 'orange',
    icon: 'Shield'
  },
  'programador': {
    label: 'Programador',
    description: 'Desenvolvimento e bugs técnicos',
    permissions: ['view_tickets', 'respond_tickets', 'assign_tickets', 'view_user_info', 'access_system_logs', 'manage_features'],
    color: 'purple',
    icon: 'Code'
  },
  'head_suporte': {
    label: 'Head Suporte',
    description: 'Supervisão de todo o suporte',
    permissions: ['view_all_tickets', 'respond_tickets', 'assign_tickets', 'view_user_info', 'manage_support_team', 'view_reports'],
    color: 'green',
    icon: 'Crown'
  },
  'head_moderacao': {
    label: 'Head Moderação',
    description: 'Supervisão de toda a moderação',
    permissions: ['view_all_tickets', 'respond_tickets', 'assign_tickets', 'view_user_info', 'moderate_content', 'ban_users', 'manage_moderation_team', 'view_reports'],
    color: 'red',
    icon: 'Crown'
  },
  'head_staff': {
    label: 'Head Staff',
    description: 'Supervisão geral de todos os departamentos',
    permissions: ['view_all_tickets', 'respond_tickets', 'assign_tickets', 'view_user_info', 'manage_all_teams', 'view_all_reports', 'system_config'],
    color: 'yellow',
    icon: 'Crown'
  },
  'direcao': {
    label: 'Direção',
    description: 'Alta cúpula da staff com acesso total',
    permissions: ['all'],
    color: 'red',
    icon: 'Crown'
  },
  'analista_qualidade': {
    label: 'Analista de Qualidade',
    description: 'Auditoria e controle de qualidade do suporte',
    permissions: ['view_all_tickets', 'view_reports', 'view_user_info', 'access_system_logs'],
    color: 'blue',
    icon: 'ShieldCheck'
  },
  'admin': {
    label: 'Administrador',
    description: 'Administração completa do sistema',
    permissions: ['all'],
    color: 'red',
    icon: 'Crown'
  }
};

// Categorias de tickets disponíveis
export const TICKET_CATEGORIES = {
  'suporte_duvidas': {
    label: 'Suporte e Dúvidas Gerais',
    department: 'suporte',
    requiredLevel: 'suporte',
    priority: 'media'
  },
  'programacao': {
    label: 'Bugs e Problemas Técnicos',
    department: 'programacao',
    requiredLevel: 'programador',
    priority: 'alta'
  },
  'compras_doacoes': {
    label: 'Compras e Doação',
    department: 'suporte',
    requiredLevel: 'suporte_compras',
    priority: 'alta'
  },
  'cupons_descontos': {
    label: 'Cupons e Descontos',
    department: 'suporte',
    requiredLevel: 'suporte_descontos',
    priority: 'media'
  },
  'denuncia_jogador': {
    label: 'Denunciar um Jogador',
    department: 'moderacao',
    requiredLevel: 'moderador',
    priority: 'alta'
  },
  'contestar_punicao': {
    label: 'Contestar Punição ou Banimento',
    department: 'moderacao',
    requiredLevel: 'super_moderador',
    priority: 'alta'
  },
  'denuncia_staff': {
    label: 'Denunciar Staff',
    department: 'admin',
    requiredLevel: 'admin',
    priority: 'alta'
  },
  'bug_report': {
    label: 'Relatório de Bug',
    department: 'programacao',
    requiredLevel: 'programador',
    priority: 'alta'
  },
  'feature_request': {
    label: 'Solicitação de Funcionalidade',
    department: 'programacao',
    requiredLevel: 'programador',
    priority: 'media'
  },
  'technical_issue': {
    label: 'Problema Técnico',
    department: 'programacao',
    requiredLevel: 'programador',
    priority: 'alta'
  }
};

// Status disponíveis para tickets
export const TICKET_STATUS = {
  'aberto': {
    label: 'Aberto',
    color: 'blue',
    description: 'Ticket criado e aguardando atendimento'
  },
  'em_andamento': {
    label: 'Em Andamento',
    color: 'yellow',
    description: 'Ticket sendo atendido por um agente'
  },
  'escalado': {
    label: 'Escalado',
    color: 'purple',
    description: 'Ticket escalado para nível superior'
  },
  'pendente': {
    label: 'Pendente',
    color: 'orange',
    description: 'Aguardando resposta do usuário'
  },
  'resolvido': {
    label: 'Resolvido',
    color: 'green',
    description: 'Ticket resolvido com sucesso'
  },
  'fechado': {
    label: 'Fechado',
    color: 'gray',
    description: 'Ticket fechado definitivamente'
  }
};

/**
 * Verifica se um usuário tem uma permissão específica
 * @param {Object} user - Objeto do usuário
 * @param {string} permission - Permissão a verificar
 * @returns {boolean} - Se tem a permissão
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.accessLevel) return false;

  const userLevel = ACCESS_LEVELS[user.accessLevel];
  if (!userLevel) return false;

  // Admin tem todas as permissões
  if (userLevel.permissions.includes('all')) return true;

  return userLevel.permissions.includes(permission);
};

/**
 * Verifica se um usuário pode acessar uma categoria de ticket
 * @param {Object} user - Objeto do usuário
 * @param {string} category - Categoria do ticket
 * @returns {boolean} - Se pode acessar
 */
export const canAccessCategory = (user, category) => {
  if (!user || !category) return false;

  const categoryInfo = TICKET_CATEGORIES[category];
  if (!categoryInfo) return false;

  // Admin pode acessar tudo
  if (user.accessLevel === 'admin') return true;

  // Head Staff pode acessar tudo exceto admin
  if (user.accessLevel === 'head_staff' && categoryInfo.department !== 'admin') {
    return true;
  }

  // Verificar se o nível do usuário pode acessar a categoria
  const userLevel = ACCESS_LEVELS[user.accessLevel];
  const requiredLevel = ACCESS_LEVELS[categoryInfo.requiredLevel];

  if (!userLevel || !requiredLevel) return false;

  // Verificar se é do mesmo departamento ou nível superior
  return user.accessLevel === categoryInfo.requiredLevel ||
    userLevel.permissions.includes('view_all_tickets');
};

/**
 * Obtém estatísticas detalhadas do sistema
 * @param {Array} tickets - Lista de tickets
 * @param {Array} users - Lista de usuários
 * @returns {Object} - Estatísticas calculadas
 */
export const getSystemStatistics = (tickets = [], users = []) => {
  const now = new Date();
  const last24Hours = new Date(now.getTime() - (24 * 60 * 60 * 1000));
  const last7Days = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
  const last30Days = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

  // Estatísticas básicas de tickets
  const ticketStats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'aberto').length,
    inProgress: tickets.filter(t => t.status === 'em_andamento').length,
    escalated: tickets.filter(t => t.status === 'escalado').length,
    pending: tickets.filter(t => t.status === 'pendente').length,
    resolved: tickets.filter(t => t.status === 'resolvido').length,
    closed: tickets.filter(t => t.status === 'fechado').length
  };

  // Tickets por período
  const periodStats = {
    last24Hours: tickets.filter(t => new Date(t.createdAt) >= last24Hours).length,
    last7Days: tickets.filter(t => new Date(t.createdAt) >= last7Days).length,
    last30Days: tickets.filter(t => new Date(t.createdAt) >= last30Days).length,
    thisMonth: tickets.filter(t => new Date(t.createdAt) >= thisMonth).length,
    lastMonth: tickets.filter(t =>
      new Date(t.createdAt) >= lastMonth && new Date(t.createdAt) <= endLastMonth
    ).length
  };

  // Tickets por departamento
  const departmentStats = {
    suporte: tickets.filter(t => {
      const category = TICKET_CATEGORIES[t.category];
      return category && category.department === 'suporte';
    }).length,
    moderacao: tickets.filter(t => {
      const category = TICKET_CATEGORIES[t.category];
      return category && category.department === 'moderacao';
    }).length,
    programacao: tickets.filter(t => {
      const category = TICKET_CATEGORIES[t.category];
      return category && category.department === 'programacao';
    }).length,
    admin: tickets.filter(t => {
      const category = TICKET_CATEGORIES[t.category];
      return category && category.department === 'admin';
    }).length
  };

  // Tickets por prioridade
  const priorityStats = {
    alta: tickets.filter(t => {
      const category = TICKET_CATEGORIES[t.category];
      return category && category.priority === 'alta';
    }).length,
    media: tickets.filter(t => {
      const category = TICKET_CATEGORIES[t.category];
      return category && category.priority === 'media';
    }).length,
    baixa: tickets.filter(t => {
      const category = TICKET_CATEGORIES[t.category];
      return category && category.priority === 'baixa';
    }).length
  };

  // Estatísticas de usuários
  const userStats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    inactive: users.filter(u => u.status === 'inactive').length,
    newThisMonth: users.filter(u => new Date(u.createdAt) >= thisMonth).length,
    byLevel: {}
  };

  // Usuários por nível de acesso
  Object.keys(ACCESS_LEVELS).forEach(level => {
    userStats.byLevel[level] = users.filter(u => u.accessLevel === level).length;
  });

  // Métricas de performance
  const performanceMetrics = {
    avgResponseTime: calculateAverageResponseTime(tickets),
    avgResolutionTime: calculateAverageResolutionTime(tickets),
    resolutionRate: ticketStats.total > 0 ? (ticketStats.resolved / ticketStats.total) * 100 : 0,
    escalationRate: ticketStats.total > 0 ? (ticketStats.escalated / ticketStats.total) * 100 : 0,
    customerSatisfaction: calculateCustomerSatisfaction(tickets)
  };

  // Crescimento mensal
  const monthlyGrowth = {
    tickets: periodStats.lastMonth > 0
      ? ((periodStats.thisMonth - periodStats.lastMonth) / periodStats.lastMonth) * 100
      : 0,
    users: calculateUserGrowth(users, thisMonth, lastMonth, endLastMonth)
  };

  return {
    tickets: ticketStats,
    periods: periodStats,
    departments: departmentStats,
    priorities: priorityStats,
    users: userStats,
    performance: performanceMetrics,
    growth: monthlyGrowth
  };
};

/**
 * Calcula tempo médio de primeira resposta
 * @param {Array} tickets - Lista de tickets
 * @returns {number} - Tempo em horas
 */
const calculateAverageResponseTime = (tickets) => {
  const ticketsWithResponse = tickets.filter(t =>
    t.responses && t.responses.length > 0
  );

  if (ticketsWithResponse.length === 0) return 0;

  const totalResponseTime = ticketsWithResponse.reduce((total, ticket) => {
    const createdAt = new Date(ticket.createdAt);
    const firstResponse = new Date(ticket.responses[0].createdAt);
    const responseTime = (firstResponse - createdAt) / (1000 * 60 * 60); // em horas
    return total + responseTime;
  }, 0);

  return totalResponseTime / ticketsWithResponse.length;
};

/**
 * Calcula tempo médio de resolução
 * @param {Array} tickets - Lista de tickets
 * @returns {number} - Tempo em horas
 */
const calculateAverageResolutionTime = (tickets) => {
  const resolvedTickets = tickets.filter(t =>
    t.status === 'resolvido' && t.resolvedAt
  );

  if (resolvedTickets.length === 0) return 0;

  const totalResolutionTime = resolvedTickets.reduce((total, ticket) => {
    const createdAt = new Date(ticket.createdAt);
    const resolvedAt = new Date(ticket.resolvedAt);
    const resolutionTime = (resolvedAt - createdAt) / (1000 * 60 * 60); // em horas
    return total + resolutionTime;
  }, 0);

  return totalResolutionTime / resolvedTickets.length;
};

/**
 * Calcula satisfação do cliente (simulado)
 * @param {Array} tickets - Lista de tickets
 * @returns {number} - Porcentagem de satisfação
 */
const calculateCustomerSatisfaction = (tickets) => {
  // Simula cálculo de satisfação baseado em tickets resolvidos
  const resolvedTickets = tickets.filter(t => t.status === 'resolvido');
  if (resolvedTickets.length === 0) return 0;

  // Simula uma satisfação entre 80-95% baseada na eficiência
  const baseScore = 85;
  const efficiencyBonus = Math.min(10, resolvedTickets.length / 10);
  return Math.min(95, baseScore + efficiencyBonus);
};

/**
 * Calcula crescimento de usuários
 * @param {Array} users - Lista de usuários
 * @param {Date} thisMonth - Início do mês atual
 * @param {Date} lastMonth - Início do mês anterior
 * @param {Date} endLastMonth - Fim do mês anterior
 * @returns {number} - Porcentagem de crescimento
 */
const calculateUserGrowth = (users, thisMonth, lastMonth, endLastMonth) => {
  const thisMonthUsers = users.filter(u => new Date(u.createdAt) >= thisMonth).length;
  const lastMonthUsers = users.filter(u =>
    new Date(u.createdAt) >= lastMonth && new Date(u.createdAt) <= endLastMonth
  ).length;

  if (lastMonthUsers === 0) return thisMonthUsers > 0 ? 100 : 0;

  return ((thisMonthUsers - lastMonthUsers) / lastMonthUsers) * 100;
};

/**
 * Gera relatório detalhado do sistema
 * @param {Array} tickets - Lista de tickets
 * @param {Array} users - Lista de usuários
 * @returns {Object} - Relatório completo
 */
export const generateSystemReport = (tickets = [], users = []) => {
  const stats = getSystemStatistics(tickets, users);
  const now = new Date();

  return {
    generatedAt: now.toISOString(),
    period: {
      start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      end: now.toISOString()
    },
    summary: {
      totalTickets: stats.tickets.total,
      totalUsers: stats.users.total,
      resolutionRate: stats.performance.resolutionRate,
      avgResponseTime: stats.performance.avgResponseTime,
      customerSatisfaction: stats.performance.customerSatisfaction
    },
    detailed: stats,
    recommendations: generateRecommendations(stats)
  };
};

/**
 * Gera recomendações baseadas nas estatísticas
 * @param {Object} stats - Estatísticas do sistema
 * @returns {Array} - Lista de recomendações
 */
const generateRecommendations = (stats) => {
  const recommendations = [];

  // Verificar tempo de resposta
  if (stats.performance.avgResponseTime > 8) {
    recommendations.push({
      type: 'warning',
      title: 'Tempo de Resposta Alto',
      description: 'O tempo médio de resposta está acima de 8 horas. Considere aumentar a equipe de suporte.',
      priority: 'alta'
    });
  }

  // Verificar taxa de resolução
  if (stats.performance.resolutionRate < 80) {
    recommendations.push({
      type: 'warning',
      title: 'Taxa de Resolução Baixa',
      description: 'A taxa de resolução está abaixo de 80%. Revise os processos de atendimento.',
      priority: 'alta'
    });
  }

  // Verificar tickets em aberto
  if (stats.tickets.open > stats.tickets.total * 0.3) {
    recommendations.push({
      type: 'info',
      title: 'Muitos Tickets Abertos',
      description: 'Há muitos tickets em aberto. Considere redistribuir a carga de trabalho.',
      priority: 'media'
    });
  }

  // Verificar crescimento de usuários
  if (stats.growth.users > 50) {
    recommendations.push({
      type: 'success',
      title: 'Crescimento Acelerado',
      description: 'O crescimento de usuários está acelerado. Prepare a infraestrutura para o aumento de demanda.',
      priority: 'media'
    });
  }

  return recommendations;
};

/**
 * Valida dados de usuário para criação/edição
 * @param {Object} userData - Dados do usuário
 * @returns {Object} - Resultado da validação
 */
export const validateUserData = (userData) => {
  const errors = [];

  // Validar campos obrigatórios
  if (!userData.name || userData.name.trim().length < 2) {
    errors.push('Nome deve ter pelo menos 2 caracteres');
  }

  if (!userData.email || !isValidEmail(userData.email)) {
    errors.push('E-mail inválido');
  }

  if (!userData.discordId || userData.discordId.trim().length < 10) {
    errors.push('Discord ID inválido');
  }

  if (!userData.accessLevel || !ACCESS_LEVELS[userData.accessLevel]) {
    errors.push('Nível de acesso inválido');
  }

  // Validar telefone se fornecido
  if (userData.phone && !isValidPhone(userData.phone)) {
    errors.push('Telefone inválido');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Valida formato de e-mail
 * @param {string} email - E-mail a validar
 * @returns {boolean} - Se é válido
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida formato de telefone brasileiro
 * @param {string} phone - Telefone a validar
 * @returns {boolean} - Se é válido
 */
const isValidPhone = (phone) => {
  const phoneRegex = /^\(\d{2}\)\s\d{4,5}-\d{4}$/;
  return phoneRegex.test(phone);
};

/**
 * Formata dados de usuário para exibição
 * @param {Object} user - Dados do usuário
 * @returns {Object} - Dados formatados
 */
export const formatUserData = (user) => {
  if (!user) return null;

  const accessLevel = ACCESS_LEVELS[user.accessLevel];

  return {
    ...user,
    accessLevelLabel: accessLevel ? accessLevel.label : user.accessLevel,
    accessLevelColor: accessLevel ? accessLevel.color : 'gray',
    statusLabel: user.status === 'active' ? 'Ativo' : 'Inativo',
    formattedCreatedAt: new Date(user.createdAt).toLocaleDateString('pt-BR'),
    formattedLastLogin: user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('pt-BR') : 'Nunca'
  };
};

export default {
  ACCESS_LEVELS,
  TICKET_CATEGORIES,
  TICKET_STATUS,
  hasPermission,
  canAccessCategory,
  getSystemStatistics,
  generateSystemReport,
  validateUserData,
  formatUserData
};
